import { State, Action, StateContext, Selector } from '@ngxs/store';
import { CompanyService } from './service';
import { ICompanyState } from './types';
import { FetchSearchAll, ChangeTrackingEnabled, SetCurrentTab, SetSearchField, SetFiltersBasedOnTab } from './actions';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { IPagination, ICompany } from '../../../../modules/authorization/store/types';
import { patch, updateItem } from '@ngxs/store/operators';

const defaults: ICompanyState = {
    isLoading: false,
    currentTab: null,
    searchField: '',
    generalCompaniesFilters: {
        page: 1,
        results: 100,
        sort: 'nameASC',
        visible: true
    },
    prospectsCompaniesFilters: {
        page: 1,
        results: 100,
        sort: 'nameASC',
        visible: false
    },
    generalCompanies: null,
    prospectsCompanies: null,
    searchCompanies: null
};

@State<ICompanyState>({
    name: 'company',
    defaults
})
@Injectable()
export class CompanyState {

    @Selector()
    static isLoading(state: ICompanyState) {
        return state.isLoading;
    }

    @Selector()
    static currentTab(state: ICompanyState) {
        return state.currentTab;
    }

    @Selector()
    static searchField(state: ICompanyState) {
        return state.searchField;
    }

    @Selector()
    static generalCompaniesFilters(state: ICompanyState) {
        return state.generalCompaniesFilters;
    }

    @Selector()
    static prospectsCompaniesFilters(state: ICompanyState) {
        return state.prospectsCompaniesFilters;
    }

    @Selector()
    static generalCompanies(state: ICompanyState) {
        return state.generalCompanies;
    }

    @Selector()
    static prospectsCompanies(state: ICompanyState) {
        return state.prospectsCompanies;
    }

    @Selector()
    static searchCompanies(state: ICompanyState) {
        return state.searchCompanies;
    }

    constructor(private companyService: CompanyService) { }

    @Action(SetCurrentTab)
    SetCurrentTab(ctx: StateContext<ICompanyState>, action: SetCurrentTab) {
        const state = ctx.getState();
        ctx.patchState({ currentTab: action.tab || state.currentTab || 'GENERAL' });
        ctx.dispatch(new SetFiltersBasedOnTab({ page: 1, ...action.filters }));
    }

    @Action(SetSearchField)
    SetSearchField(ctx: StateContext<ICompanyState>, action: SetSearchField) {
        ctx.patchState({ searchField: action.value });
        ctx.dispatch(new SetFiltersBasedOnTab({ page: 1 }));
    }

    @Action(SetFiltersBasedOnTab)
    SetFiltersBasedOnTab(ctx: StateContext<ICompanyState>, action: SetFiltersBasedOnTab) {
        let state = ctx.getState();
        ctx.patchState({ isLoading: true });
        switch (state.currentTab) {
            case 'PROSPECTS':
                ctx.patchState({
                    prospectsCompaniesFilters: {
                        ...state.prospectsCompaniesFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.companyService.fetchAllCompanies({ ...state.prospectsCompaniesFilters, search: state.searchField })
                    .pipe(tap((pagination: IPagination<ICompany>) => ctx.patchState({ prospectsCompanies: pagination, isLoading: false })));
            default:
                ctx.patchState({
                    generalCompaniesFilters: {
                        ...state.generalCompaniesFilters,
                        ...action.filters
                    }
                });
                state = ctx.getState();
                return this.companyService.fetchAllCompanies({ ...state.generalCompaniesFilters, search: state.searchField })
                    .pipe(tap((pagination: IPagination<ICompany>) => ctx.patchState({ generalCompanies: pagination, isLoading: false })));
        }
    }

    @Action(ChangeTrackingEnabled)
    ChangeTrackingEnabled(ctx: StateContext<ICompanyState>, action: ChangeTrackingEnabled) {
        return this.companyService.update(action.companyID, { timeTrackingEnabled: action.status })
            .pipe(
                tap(() => {
                    ctx.setState(
                        patch({
                            generalCompanies: patch({
                                results: updateItem<ICompany>(el => el._id === action.companyID, patch({
                                    timeTrackingEnabled: action.status
                                }))
                            })
                        })
                    );
                    ctx.setState(
                        patch({
                            prospectsCompanies: patch({
                                results: updateItem<ICompany>(el => el._id === action.companyID, patch({
                                    timeTrackingEnabled: action.status
                                }))
                            })
                        })
                    );
                })
            );
    }

    @Action(FetchSearchAll)
    FetchSearchAll(ctx: StateContext<ICompanyState>, action: FetchSearchAll) {
        return this.companyService.fetchSearchAll(action.data)
            .pipe(tap((pagination: IPagination) => ctx.patchState({ searchCompanies: pagination })));
    }

}
